<template>
  <div
    :class="[
      'bdp-panel',
      { 'bdp-excluded': isExcluded, 'bdp-archived': bom.isArchived },
    ]"
  >
    <!-- Collapsible header -->
    <div class="bdp-hdr" @click="expanded = !expanded">
      <div class="bdp-hdr-left">
        <span class="bdp-chevron">{{ expanded ? "▼" : "▶" }}</span>
        <div>
          <div class="bdp-title">{{ bom.bomCode }} — {{ bom.productName }}</div>
          <div class="bdp-sub">
            {{ bom.bomStatus == "X" ? "Inactive" : "Active"
            }}<template v-if="bom.dateChanged">
              · {{ formatDate(bom.dateChanged) }}</template
            >
            · {{ bom.diffLines.length }} lines total
          </div>
        </div>
      </div>
      <div class="bdp-hdr-right">
        <span v-if="isExcluded" class="bdp-badge bdp-badge-excluded"
          >Excluded</span
        >
        <span
          v-if="bom.isArchived && !isExcluded"
          class="bdp-badge bdp-badge-archived"
          >Archived</span
        >
        <!-- <span
          v-if="isInactive && !isExcluded && !bom.isArchived"
          class="bdp-badge bdp-badge-inactive"
          >⚠ Archived BOM</span
        > -->
        <template v-if="!isExcluded">
          <span
            v-for="(pill, i) in summaryPills"
            :key="i"
            :class="['bdp-pill', 'bdp-pill-' + pill.color]"
            >{{ pill.text }}</span
          >
        </template>
      </div>
    </div>

    <!-- Expanded body -->
    <div v-if="expanded" class="bdp-body">
      <!-- Archived warning -->
      <div v-if="bom.isArchived" class="bdp-warn bdp-warn-archived">
        ⚠ This BOM is archived. Applying changes will not alter its archived
        status. Verify this is intentional.
      </div>

      <!-- Inactive warning -->
      <div v-if="isInactive" class="bdp-warn bdp-warn-archived">
        ⚠ This BOM is inactive. Applying changes will not alter its inactive
        status. Verify this is intentional.
      </div>

      <!-- Linked-material warning -->
      <div v-if="bom.hasLinkedMaterialWarning" class="bdp-warn bdp-warn-linked">
        ⚠ This BOM has processes with linked material lines. Process replacement
        operations that target these processes will fail for this BOM — reassign
        or remove the linked materials first.
      </div>

      <!-- Diff table -->
      <div class="bdp-dtable">
        <div class="bdp-dtable-head">
          <!-- <div class="bdp-dth bdp-col-proc">Process Code</div> -->
          <div class="bdp-dth bdp-col-type">Type</div>
          <div class="bdp-dth bdp-col-code">Code</div>
          <div class="bdp-dth bdp-col-dsc">Description</div>
          <div class="bdp-dth bdp-col-cur">Current</div>
          <div class="bdp-dth bdp-col-new">New</div>
          <div class="bdp-dth bdp-col-unit">UOM</div>
          <div class="bdp-dth bdp-col-chg">Change</div>
        </div>
        <div
          v-for="(line, i) in visibleLines"
          :key="`${line.processCode}-${line.code}-${i}`"
          :class="['bdp-dtr', rowClass(line.changeType)]"
        >
          <!-- <div class="bdp-dtc bdp-col-proc bdp-muted-text">
            {{ line.processCode || "—" }}
          </div> -->

          <div class="bdp-dtc bdp-col-type">
            <span
              :class="[
                'bdp-type-badge',
                line.lineType === 'Material'
                  ? 'bdp-badge-mat'
                  : 'bdp-badge-proc',
              ]"
              >{{ line.lineType === "Material" ? "Mat" : "Proc" }}</span
            >
          </div>
          <div
            :class="[
              'bdp-dtc',
              'bdp-col-code',
              { 'bdp-code-bold': isChangedType(line.changeType) },
            ]"
          >
            {{ line.code }}
          </div>
          <div
            :class="[
              'bdp-dtc',
              'bdp-col-dsc',
              { 'bdp-na': line.changeType === 'NotApplicable' },
            ]"
          >
            {{ line.description }}
          </div>
          <div class="bdp-dtc bdp-col-cur">
            <span
              v-if="line.currentValue != null"
              :class="{ 'bdp-old-val': isChangedType(line.changeType) }"
              >{{ formatValue(line.currentValue) }}</span
            >
            <span v-else class="bdp-muted-text">—</span>
          </div>
          <div class="bdp-dtc bdp-col-new">
            <template v-if="line.changeType === 'Replaced' && line.newCode">
              <span class="bdp-replaced-code">{{ line.newCode }}</span>
            </template>
            <template v-else-if="line.newValue != null">
              <span
                :class="{ 'bdp-new-val': isChangedType(line.changeType) }"
                >{{ formatValue(line.newValue) }}</span
              >
              <span
                v-if="line.newValue === 0"
                class="bdp-zero-warn"
                title="Line will be preserved with zero quantity — it will not be removed"
                >⚠ Zero qty</span
              >
            </template>
            <span v-else class="bdp-muted-text">—</span>
          </div>
          <div class="bdp-dtc bdp-col-unit">{{ line.unitOfMeasure }}</div>
          <div class="bdp-dtc bdp-col-chg">
            <span
              :class="['bdp-pill', 'bdp-pill-' + pillColor(line.changeType)]"
              >{{ changeLabel(line.changeType) }}</span
            >
          </div>
        </div>
      </div>

      <!-- Panel footer -->
      <div class="bdp-foot">
        <span>
          {{ affectedCount }} of {{ bom.diffLines.length }} lines affected
          <template v-if="notApplicableCount > 0">
            · {{ notApplicableCount }} skipped</template
          >
        </span>
        <span
          v-if="!isReadOnly"
          :class="isExcluded ? 'bdp-incl-wrap' : 'bdp-excl-wrap'"
        >
          <DxButton
            :text="isExcluded ? 'Include this BOM' : 'Exclude this BOM'"
            type="normal"
            styling-mode="outlined"
            class="bdp-toggle-btn"
            @click="onToggleExclusion"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import { formatNumber } from "devextreme/localization";

const CHANGED_TYPES = [
  "QtyChanged",
  "RunRateChanged",
  "Added",
  "Replaced",
  "Removed",
];

export default {
  name: "GcBomBulkUpdateBomDiffPanel",
  components: { DxButton },
  props: {
    bom: { type: Object, required: true },
    isExcluded: { type: Boolean, default: false },
    isExpanded: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    lineFilter: { type: String, default: null },
  },
  data() {
    return {
      expanded: this.isExpanded,
    };
  },
  computed: {
    isInactive() {
      return (
        (this.bom.bomStatus &&
          this.bom.bomStatus.toLowerCase() === "inactive") ||
        this.bom.bomStatus.toLowerCase() === "x"
      );
    },
    visibleLines() {
      if (!this.lineFilter) return this.bom.diffLines;
      return this.bom.diffLines.filter((l) => l.lineType === this.lineFilter);
    },
    summaryPills() {
      const lines = this.bom.diffLines;
      const changed = lines.filter((l) =>
        ["QtyChanged", "RunRateChanged"].includes(l.changeType),
      ).length;
      const added = lines.filter((l) => l.changeType === "Added").length;
      const replaced = lines.filter((l) => l.changeType === "Replaced").length;
      const removed = lines.filter((l) => l.changeType === "Removed").length;
      const zeroQty = lines.filter((l) => l.newValue === 0).length;

      const pills = [];
      if (changed)
        pills.push({
          text: `${changed} qty/rate change${changed > 1 ? "s" : ""}`,
          color: "amber",
        });
      if (added)
        pills.push({
          text: `${added} line${added > 1 ? "s" : ""} added`,
          color: "green",
        });
      if (replaced)
        pills.push({
          text: `${replaced} code${replaced > 1 ? "s" : ""} added`,
          color: "green",
        });
      if (removed)
        pills.push({
          text: `${removed} line${removed > 1 ? "s" : ""} removed`,
          color: "red",
        });
      if (zeroQty)
        pills.push({
          text: `${zeroQty} set to zero ⚠`,
          color: "amber",
        });
      return pills;
    },
    affectedCount() {
      return this.bom.diffLines.filter(
        (l) => !["NoChange", "NotApplicable"].includes(l.changeType),
      ).length;
    },
    notApplicableCount() {
      return this.bom.diffLines.filter((l) => l.changeType === "NotApplicable")
        .length;
    },
  },
  methods: {
    onToggleExclusion() {
      this.$emit(this.isExcluded ? "include" : "exclude", this.bom.bomCode);
    },

    isChangedType(changeType) {
      return CHANGED_TYPES.includes(changeType);
    },
    rowClass(changeType) {
      if (["QtyChanged", "RunRateChanged"].includes(changeType))
        return "bdp-row-changed";
      if (changeType === "Added" || changeType === "Replaced")
        return "bdp-row-added";
      if (["Removed"].includes(changeType)) return "bdp-row-removed";
      if (["NoChange", "NotApplicable"].includes(changeType))
        return "bdp-row-nochange";
      return "";
    },
    pillColor(changeType) {
      if (["QtyChanged", "RunRateChanged"].includes(changeType)) return "amber";
      if (changeType === "Added" || changeType === "Replaced") return "green";
      if (["Removed"].includes(changeType)) return "red";
      return "grey";
    },
    changeLabel(changeType) {
      const labels = {
        QtyChanged: "Qty changed",
        RunRateChanged: "Run rate changed",
        Added: "Line added",
        Replaced: "Added",
        Removed: "Removed",
        NotApplicable: "N/A — skipped",
        NoChange: "No change",
      };
      return labels[changeType] || changeType;
    },
    formatValue(value) {
      if (value == null) return "—";
      if (typeof value === "string") return value;
      return formatNumber(value, "##,###,###,###,##0.0000");
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toLocaleDateString("en-AU", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return dateStr;
      }
    },
  },
};
</script>

<style lang="scss">
.bdp-panel {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.12s;

  &:hover {
    border-color: #b0d4ea;
  }

  &.bdp-excluded {
    opacity: 0.55;
    border-style: dashed;
  }
}

// ── Header ────────────────────────────────────────────────────────────────────
.bdp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  background: #fafafa;
  user-select: none;

  &:hover {
    background: #f0f8ff;
  }
}

.bdp-hdr-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.bdp-chevron {
  font-size: 10px;
  color: #6c757d;
  flex-shrink: 0;
}

.bdp-title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.bdp-sub {
  font-size: 11px;
  color: #6c757d;
  margin-top: 1px;
}

.bdp-hdr-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

// ── Status badges ─────────────────────────────────────────────────────────────
.bdp-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.bdp-badge-excluded {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #adb5bd;
}

.bdp-badge-archived {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

.bdp-badge-inactive {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

// ── Summary pills ─────────────────────────────────────────────────────────────
.bdp-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.bdp-pill-amber {
  background: #fef0c7;
  color: #92400e;
  border: 1px solid #f79009;
}

.bdp-pill-green {
  background: #d1fadf;
  color: #065f46;
  border: 1px solid #17b26a;
}

.bdp-pill-red {
  background: #fee4e2;
  color: #9b1c1c;
  border: 1px solid #f97066;
}

.bdp-pill-grey {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

// ── Body ──────────────────────────────────────────────────────────────────────
.bdp-body {
  border-top: 1px solid #dee2e6;
}

// ── Warnings ──────────────────────────────────────────────────────────────────
.bdp-warn {
  padding: 8px 12px;
  font-size: 12px;
  border-bottom: 1px solid;
}

.bdp-warn-archived {
  background: #fff8e1;
  border-color: #ffe082;
  color: #7c5c0a;
}

.bdp-warn-linked {
  background: #fff3f3;
  border-color: #f5c6cb;
  color: #842029;
}

// ── Diff table ────────────────────────────────────────────────────────────────
.bdp-dtable {
  width: 100%;
  font-size: 12px;
  overflow-x: auto;
}

.bdp-dtable-head {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.bdp-dth {
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.bdp-dtr {
  display: flex;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &.bdp-row-changed {
    background: #fffbeb;
  }

  &.bdp-row-added {
    background: #f0fdf4;
  }

  &.bdp-row-removed {
    background: #fff5f5;
  }

  &.bdp-row-nochange {
    background: #f8f9fa;
    color: #9ca3af;
  }
}

.bdp-dtc {
  padding: 6px 8px;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ── Column widths ─────────────────────────────────────────────────────────────
.bdp-col-proc {
  width: 90px;
  flex-shrink: 0;
}

.bdp-col-code {
  width: 110px;
  flex-shrink: 0;
}

.bdp-col-type {
  width: 58px;
  flex-shrink: 0;
}

.bdp-col-dsc {
  flex: 1;
  min-width: 150px;
  white-space: normal;
}

.bdp-col-cur {
  width: 90px;
  flex-shrink: 0;
  text-align: right;
}

.bdp-col-new {
  width: 130px;
  flex-shrink: 0;
  text-align: right;
}

.bdp-col-unit {
  width: 65px;
  flex-shrink: 0;
}

.bdp-col-chg {
  width: 130px;
  flex-shrink: 0;
}

// ── Type badges ───────────────────────────────────────────────────────────────
.bdp-type-badge {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.bdp-badge-mat {
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #b3e5fc;
}

.bdp-badge-proc {
  background: #ede8fb;
  color: #6a4cb0;
  border: 1px solid #c8b8f0;
}

// ── Value styling ─────────────────────────────────────────────────────────────
.bdp-code-bold {
  font-weight: 600;
}

.bdp-old-val {
  color: #6c757d;
  text-decoration: line-through;
}

.bdp-new-val {
  font-weight: 600;
  color: #101828;
}

.bdp-replaced-code {
  font-weight: 600;
  color: #dc3545;
}

.bdp-muted-text {
  color: #adb5bd;
}

.bdp-na {
  color: #adb5bd;
  font-style: italic;
  font-size: 11px;
}

.bdp-zero-warn {
  color: #856404;
  font-size: 0.85em;
  margin-left: 4px;
  white-space: nowrap;
}

// ── Footer ────────────────────────────────────────────────────────────────────
.bdp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  font-size: 12px;
  color: #6c757d;
}

.bdp-toggle-btn {
  font-size: 12px;
}

.bdp-incl-wrap .dx-button {
  color: #17b26a;
  border-color: #17b26a;

  &:hover {
    background: #f0fdf4;
  }
}
</style>
